import type { Cti, FormData } from "@amzn/aquila-service-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import { isUndefined } from "lodash";
import { useCallback, useEffect } from "react";
import { useStore } from "zustand";

import { getPublisherPortalApiClient } from "../../../../api/publisherPortalApi/publisherPortalApi";
import {
    DEFAULT_REPORT_ISSUE_CTI,
    REPORT_ISSUE_RESOLVER_GROUP_MAP,
} from "../../../../config/reportIssueCti.config";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { reportIssueStore } from "../../../../store/reportIssue";
import { AquilaReportIssueTriggerType } from "../../../../types/aquila";
import { TopNavBarTabs } from "../../../../types/navigation";
import { isError } from "../../../../utils/api.utils";

const getReportIssueCti = (key: string): Cti => {
    return {
        ...DEFAULT_REPORT_ISSUE_CTI,
        resolverGroup: REPORT_ISSUE_RESOLVER_GROUP_MAP[key],
    };
};

export const useReportIssueCti = (
    trigger: AquilaReportIssueTriggerType,
): [(formData: FormData[]) => Promise<Cti>] => {
    const [reportIssueContextData] = useStore(reportIssueStore, (state) => [
        state.contextData,
    ]);
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [domainAsyncState, domainAsyncAction] = useAsync(
        async (domainIdentifier: string) => {
            if (domainIdentifier) {
                const { data } = await (
                    await getPublisherPortalApiClient()
                ).getDomain({
                    domainIdentifier,
                });
                return data;
            }
        },
    );

    const executeDomainAction = async (
        currentDomainIdentifier: string,
    ): Promise<Cti | undefined> => {
        const result = await domainAsyncAction.execute(currentDomainIdentifier);
        if (result?.cti) {
            const { category, type, item, resolverGroup } = result.cti;
            if (
                !isUndefined(type) &&
                !isUndefined(category) &&
                !isUndefined(resolverGroup) &&
                !isUndefined(item)
            ) {
                if (REPORT_ISSUE_RESOLVER_GROUP_MAP[resolverGroup]) {
                    return getReportIssueCti(resolverGroup);
                }

                return {
                    type,
                    category,
                    resolverGroup,
                    item,
                } as Cti;
            }
        }
    };

    const getIssueType = (formData) => {
        return formData.find((f) => f.field === "issueType")?.value;
    };

    const getContentType = (formData) => {
        return formData.find((f) => f.field === "contentType")?.value;
    };

    const getStandaloneInsightType = (formData) => {
        return formData.find((f) => f.field === "standaloneInsightType")?.value;
    };
    const getPackagedInsightType = (formData) => {
        return formData.find((f) => f.field === "packagedInsightType")?.value;
    };

    const getTemplateDomainId = (formData) => {
        return formData.find((f) => f.field === "templateDomainId")?.value;
    };

    const getCtiForAllInsightsIssues = useCallback(
        async (formData: FormData[]) => {
            let cti: Cti | undefined = DEFAULT_REPORT_ISSUE_CTI;
            const issueType = getIssueType(formData);
            const contentType = getContentType(formData);
            const standaloneInsightType = getStandaloneInsightType(
                formData,
            ) as string;
            const packagedInsightType = getPackagedInsightType(formData);
            const templateDomainUrl = getTemplateDomainId(formData);
            const templateDomainId =
                templateDomainUrl?.match(/domains\/([\w-]+)/)?.[1];

            if (trigger === AquilaReportIssueTriggerType.ALL_INSIGHTS_TAB) {
                if (issueType === "Cannot find insight") {
                    if (contentType === "Standalone insight") {
                        cti = getReportIssueCti(standaloneInsightType);
                    } else if (
                        contentType === "Packaged insight" &&
                        packagedInsightType === "CAIRE"
                    ) {
                        cti = getReportIssueCti("CAIRE");
                    } else if (
                        contentType === "Packaged insight" &&
                        packagedInsightType !== "CAIRE" &&
                        templateDomainId
                    ) {
                        cti = await executeDomainAction(
                            templateDomainId as string,
                        );
                    } else if (contentType === "Marketing content") {
                        cti = getReportIssueCti("Titan");
                    }
                } else if (issueType === "Brand or Browse Node not found") {
                    cti = getReportIssueCti("InsightsDefault");
                } else if (issueType === "Search or Filter Problems") {
                    cti = getReportIssueCti("IHub");
                } else if (issueType === "Cannot access desired SF account") {
                    cti = getReportIssueCti("Titan");
                } else if (issueType === "Issues with AMC SIP insights") {
                    cti = getReportIssueCti("SIP");
                }
            } else if (
                trigger === AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL
            ) {
                if (
                    reportIssueContextData.insightType === InsightType.INSIGHT
                ) {
                    cti = getReportIssueCti(
                        reportIssueContextData.standaloneInsightType,
                    );
                } else if (
                    reportIssueContextData.insightType === InsightType.CAIRE
                ) {
                    cti = getReportIssueCti("CAIRE");
                } else if (
                    reportIssueContextData.insightType === InsightType.MARKETING
                ) {
                    cti = getReportIssueCti("Titan");
                }
            } else if (
                trigger ===
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_CREATE_ON_DEMAND &&
                reportIssueContextData.domainIdPathParam
            ) {
                cti = await executeDomainAction(
                    reportIssueContextData.domainIdPathParam as string,
                );
            }
            return cti ?? DEFAULT_REPORT_ISSUE_CTI;
        },
        [trigger, reportIssueContextData, useUnifiedSearch],
    );

    const getCtiForYourInsightDecksIssues = useCallback(
        async (formData: FormData[]) => {
            const issueType = getIssueType(formData);
            if (issueType === "Other") {
                return DEFAULT_REPORT_ISSUE_CTI;
            }

            let cti: Cti = DEFAULT_REPORT_ISSUE_CTI;
            if (
                trigger === AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_TAB
            ) {
                if (issueType === "Cannot find my insight or content") {
                    cti = DEFAULT_REPORT_ISSUE_CTI;
                }
            } else if (
                trigger ===
                AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL
            ) {
                if (
                    reportIssueContextData.insightType ===
                    InsightType.PITCHED_CONTENT
                ) {
                    cti = getReportIssueCti("Titan");
                } else {
                    const domainCti = await executeDomainAction(
                        reportIssueContextData.domainIdentifier as string,
                    );
                    if (domainCti) {
                        cti = domainCti;
                    }
                }
            }
            return cti;
        },
        [trigger, reportIssueContextData],
    );

    const getCti = useCallback(
        async (formData: FormData[]) => {
            let cti: Cti = DEFAULT_REPORT_ISSUE_CTI;
            if (
                reportIssueContextData.activeTab === TopNavBarTabs.ALL_INSIGHTS
            ) {
                cti = await getCtiForAllInsightsIssues(formData);
            } else if (
                reportIssueContextData.activeTab ===
                TopNavBarTabs.YOUR_INSIGHT_DECKS
            ) {
                cti = await getCtiForYourInsightDecksIssues(formData);
            }

            return cti ?? DEFAULT_REPORT_ISSUE_CTI;
        },
        [
            trigger,
            reportIssueContextData,
            getCtiForAllInsightsIssues,
            getCtiForYourInsightDecksIssues,
        ],
    );

    useEffect(() => {
        if (isError(domainAsyncState.status)) {
            console.error(domainAsyncState.error);
        }
    }, [domainAsyncState]);

    return [getCti];
};
